function normalizeImageUrl(u) {
  if (u == null || typeof u !== "string") return null;
  const t = u.trim();
  if (!t) return null;
  if (t.startsWith("//")) return `https:${t}`;
  if (t.startsWith("http://") || t.startsWith("https://")) return t;
  return null;
}

/**
 * Defensive normalization for customer-chat hotel cards (API may omit fields).
 * @param {Record<string, unknown>} card
 * @returns {{ mainImage: string|null, galleryImages: string[], thumbImages: string[] }}
 */
function urlsFromGalleryItem(item) {
  if (item == null) return [];
  if (typeof item === "string") {
    const u = normalizeImageUrl(item);
    return u ? [u] : [];
  }
  if (typeof item === "object") {
    const u =
      item.imageurl ||
      item.imageUrl ||
      item.ImageUrl ||
      item.url ||
      item.Url ||
      item.FullSize;
    const nu = normalizeImageUrl(typeof u === "string" ? u : null);
    return nu ? [nu] : [];
  }
  return [];
}

export function normalizeCustomerChatHotelCard(card) {
  const rawGallery = card?.gallery_images;
  const list = Array.isArray(rawGallery)
    ? rawGallery.flatMap((u) => urlsFromGalleryItem(u))
    : [];
  const primary =
    normalizeImageUrl(typeof card?.main_image === "string" ? card.main_image : null) ||
    normalizeImageUrl(typeof card?.image === "string" ? card.image : null) ||
    null;

  const deduped = [];
  const push = (u) => {
    const t = normalizeImageUrl(u);
    if (!t || deduped.includes(t)) return;
    deduped.push(t);
  };

  push(primary);
  for (const u of list) push(u);

  const mainImage = deduped[0] || null;
  const galleryImages = deduped.length ? deduped : mainImage ? [mainImage] : [];
  const thumbImages = mainImage ? galleryImages.slice(1, 5) : galleryImages.slice(0, 4);

  return { mainImage, galleryImages, thumbImages };
}

/** Lowercase + collapse spaces — compare API title vs hotel_name */
export function comparableChatHotelName(s) {
  return String(s ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

/** Same key order as toPackageViewModel (fullDeal.hotel) + legacy PascalCase */
const LDS_HOTEL_STAR_KEYS = [
  "stars",
  "starRating",
  "hotelStars",
  "category",
  "Rating",
  "rating",
  "StarRating",
  "StarCategory",
  "Category",
];

function hotelFromSearchDealRaw(sp) {
  if (!sp || typeof sp !== "object") return null;
  const raw = sp.search_deal_raw;
  if (!raw || typeof raw !== "object") return null;
  const fd = typeof raw.fullDeal === "object" ? raw.fullDeal : raw;
  const h = fd.hotel;
  return h && typeof h === "object" ? h : null;
}

function hotelFromExtendedBlocks(sp) {
  if (!sp || typeof sp !== "object") return null;
  for (const k of ["extended_deal_normalized", "extended_deal_raw"]) {
    const ext = sp[k];
    if (ext && typeof ext === "object") {
      const h = ext.hotel;
      if (h && typeof h === "object") return h;
    }
  }
  return null;
}

function parseStarishValue(v) {
  if (v == null || v === "") return null;
  const s = String(v).replace(/\+/g, "").trim();
  const n = Number(s);
  if (Number.isFinite(n)) {
    const r = Math.round(n);
    if (r >= 1 && r <= 5) return r;
  }
  for (let i = 0; i < s.length; i += 1) {
    const ch = s[i];
    if (ch >= "1" && ch <= "5") return parseInt(ch, 10);
    if (ch >= "6" && ch <= "9") break;
  }
  return null;
}

function starsFromLdsHotelDict(h) {
  if (!h || typeof h !== "object") return null;
  for (const k of LDS_HOTEL_STAR_KEYS) {
    const v = parseStarishValue(h[k]);
    if (v != null) return v;
  }
  return null;
}

/**
 * Star count for chat hotel card — same hotel sources as B2C package cards (search_deal_raw → fullDeal.hotel).
 * @param {Record<string, unknown>|null|undefined} card
 * @returns {number|null} 1–5 or null
 */
export function resolveChatCardStarCount(card) {
  let v =
    parseStarishValue(card?.stars) ??
    parseStarishValue(card?.starRating) ??
    parseStarishValue(card?.category) ??
    parseStarishValue(card?.hotelStars);
  if (v != null) return v;

  const sp = card?.source_payload_json;
  if (sp && typeof sp === "object") {
    v = starsFromLdsHotelDict(hotelFromSearchDealRaw(sp));
    if (v != null) return v;
    v = starsFromLdsHotelDict(hotelFromExtendedBlocks(sp));
    if (v != null) return v;
  }

  return null;
}
